import type { SUPPORTED_REGION } from './region'

// Whether an account is still open on the credit file. The AU Equifax file has no
// account-status element, so this is derived from the presence of a closed date.
export enum CREDIT_FILE_ACCOUNT_STATUS {
  ACTIVE = 'Active',
  CLOSED = 'Closed',
}

// A vehicle loan (Equifax account type AL) found on the credit file, whether it is
// still active or has been closed.
export interface CreditFileVehicleLoan {
  creditProvider: string
  status: CREDIT_FILE_ACCOUNT_STATUS
}

export interface CreditDataAU {
  region: SUPPORTED_REGION.AU
  loanId?: number
  userId?: number
  assessmentId?: number
  createdAt?: string
  comprehensiveScore: string
  vedaScore: string
  equifaxOneScore: string
  creditFileAge: string
  creditEnquiries6m: number
  creditEnquiries12m: number
  paydayEnquiries: ''
  rhi12m: string
  rhi24m: string
  rhi3m: string
  rhi6m: string
  rhi3mCC: string
  rhi3mNCC: string
  rhi12mCC: string
  rhi12mNCC: string
  hmrrhi: string

  openCreditCards: number
  openPersonalLoans: number
  openMortgages: number
  openMortgagesTotal: number
  openVehicleLoans: number
  defaultJudgements: number
  equifaxOpenOverDraft: number

  outstandingDefaultJudgementsPast5YearCount: number
  outstandingDefaultJudgementsPast5YearsTotal: number
  outstandingFinancialDefaultsPast6MonthsCount: number
  outstandingFinancialDefaultsPast6MonthsTotal: number

  paidDefaultJudgementsPastYearCount: number
  paidDefaultJudgementsPastYearTotal: number

  paidDefaultJudgementsCount: number
  paidDefaultJudgementsTotal: number

  outstandingCommercialDefaultsPast6MonthsCount: number
  outstandingCommercialDefaultsPast6MonthsTotal: number

  paidNonFinancialDefaultsPast6MonthsCount: number
  paidNonFinancialDefaultsPast6MonthsTotal: number

  outstandingFinancialDefaultsCount: number
  outstandingFinancialDefaultsTotal: number
  outstandingNonFinancialDefaultsCount: number
  outstandingNonFinancialDefaultsTotal: number
  outstandingNonFinancialDefaultsPastYearCount: number
  outstandingNonFinancialDefaultsPastYearTotal: number
  outstandingCommercialDefaultsCount: number
  outstandingCommercialDefaultsTotal: number
  paidFinancialDefaultsCount: number
  paidFinancialDefaultsTotal: number
  paidFinancialDefaultsPastYearCount: number
  paidFinancialDefaultsPastYearTotal: number
  paidNonFinancialDefaultsCount: number
  paidNonFinancialDefaultsTotal: number
  paidNonFinancialDefaultsPastYearCount: number
  paidNonFinancialDefaultsPastYearTotal: number
  paidCommercialDefaultsCount: number
  paidCommercialDefaultsTotal: number
  paidCommercialDefaultsPastYearCount: number
  paidCommercialDefaultsPastYearTotal: number
  paydayEnquiriesCount6m: number
  paydayEnquiriesCount12m: number
  paydayEnquiriesCount24m: number
  consumerEnquiriesPastMonthCount: number
  businessCashFlowCreditEnquiries6m: number
  businessCashFlowCreditEnquiries12m: number
  possibleMatch: number
  openOverdraftLimits: number
  hasOverdraftAccount: number

  allNonFinancialDefaultsCount: number
  allNonFinancialDefaultsTotal: number
  allNonFinancialDefaultsPast6MonthsCount: number
  allNonFinancialDefaultsPast6MonthsTotal: number

  fhi: number // 1 or 0
  tfhi: number // 1 or 0
  fhiContractualVariationAccounts12MonthCount: number
  fhiTimeSinceLastTemporaryArrangement12Month: number

  thinFileAccountsAndRepaymentsCount: number
  thinFileAccountsAndRepaymentsTotal: number
  openCreditCardLenderNames: string[]
  openMortgageLenderNames: string[]
  openPersonalLoanLenderNames: string[]
  openVehicleLoanLenderNames: string[]
  // Every vehicle loan on the file, active and closed, with its credit provider.
  creditFileVehicleLoans: CreditFileVehicleLoan[]
  // 1 when the file shows a BNPL account (Equifax type BF or BT) that was open at any
  // point in the last 12 months, whether or not it is still open today.
  hasBnplOnCreditFile12m: number // 1 or 0

  outstandingDefaultCount: number
  outstandingDefaultCreditProviders: string[]

  isCurrentDirectorshipUnderExternalAdmin: number
  isCurrentDirectorshipUnderStrikeOff: number

  unsecuredEnquiriesCount12m: number
  creditEnquiries1m: number

  paidDefaultCount: number
  paidOrOutstandingDefaultCount6m: number
  latestThinFileReportedRepayment6m: string
  latestThinFileReportedRepaymentMinusOpenDate6m: string

  isCurrentDirectorshipActive: number // 1 or 0
  isDetectedABNInCurrentDirectorships: number | null

  openUnsecuredFacilities6mCount: number
  openUnsecuredFacilities24mCount: number

  isRhi24mCCRMPL: number // 1 or 0
  highestRhi5k: string

  rhiWithoutTelcoAndUtility6m: string
}

export interface CreditDataNZ {
  region: SUPPORTED_REGION.NZ

  // Equifax
  equifaxOneScore?: number
  equifaxScore?: number
  vedaScore?: number
  comprehensiveScore?: number

  creditFileAge: number
  creditEnquiries6m: number
  creditEnquiries12m: number
  paydayEnquiriesCount12m: number
  rhi24m: number | string

  outstandingFinancialDefaultsCount: number
  outstandingFinancialDefaultsTotal: number
  outstandingNonFinancialDefaultsCount: number
  outstandingNonFinancialDefaultsTotal: number
  openOverdraftLimits?: number

  paidFinancialDefaultsCount: number
  paidFinancialDefaultsTotal: number
  paidNonFinancialDefaultsCount: number
  paidNonFinancialDefaultsTotal: number

  defaultJudgementsCount: number
  defaultJudgementsTotal: number

  openCreditCards: number
  openPersonalLoans: number
  openMortgages: number
  openVehicleLoans: number
  openMortgagesTotal: number
  equifaxOpenOverDraft?: number
  centrixOpenOverDraft?: number

  rhi3m: string
  rhi6m: string
  rhi3mCC: string
  rhi3mNCC: string
  rhi12m: string
  rhi12mCC: string
  rhi12mNCC: string
  hmrrhi: string

  fhi: number

  paidDefaultJudgementsCount: number
  paidDefaultJudgementsTotal: number
  paidDefaultJudgementsCreditProviders: string[]

  outstandingDefaultJudgementsCount: number
  outstandingDefaultJudgementsCreditProviders: string[]

  outstandingDefaultCount: number
  outstandingDefaultCreditProviders: string[]

  rhiWithoutTelcoAndUtility6m: string

  outstandingMOJCount: number
  outstandingMOJTotal: number

  // Centrix
  centrixScore?: number
  centrixCreditFileAge?: number

  centrixOpenCreditCards?: number
  centrixOpenPersonalLoans?: number
  centrixOpenMortgages?: number
  centrixOpenVehicleLoans?: number

  centrixThinFileAccountsAndRepaymentsCount?: number

  centrixOutstandingDefaultJudgementsCount?: number
  centrixOutstandingDefaultJudgementsTotal?: number
  centrixOutstandingDefaultJudgementsCreditProviders?: string[]

  centrixCreditEnquiries6m?: number
  centrixCreditEnquiries12m?: number
  centrixBusinessCashFlowCreditEnquiries6m?: number
  centrixBusinessCashFlowCreditEnquiries12m?: number

  centrixRhi24m?: number | string
  centrixHmrrhi?: string | number

  centrixOutstandingFinancialDefaultsCount?: number
  centrixOutstandingFinancialDefaultsTotal?: number
  centrixOutstandingNonFinancialDefaultsCount?: number
  centrixOutstandingNonFinancialDefaultsTotal?: number

  centrixPaidFinancialDefaultsCount?: number
  centrixPaidFinancialDefaultsTotal?: number
  centrixPaidNonFinancialDefaultsCount?: number
  centrixPaidNonFinancialDefaultsTotal?: number

  centrixRhi3m?: number | string
  centrixRhi6m?: number | string
  centrixRhi12m?: number | string

  centrixFhi?: number

  centrixPaidDefaultJudgementsCount?: number
  centrixPaidDefaultJudgementsTotal?: number
  centrixPaidDefaultJudgementsCreditProviders?: string[]

  centrixOutstandingMOJCount?: number
  centrixOutstandingMOJTotal?: number
}
